// @flow
import React, { Component } from "react";
import { connect } from "react-redux";

import type { StateType, DispatchType } from "../../../global/types/redux";
import type { ErrorsType } from "../../types/UserFormType";
import {
  StyledTextField,
  StyledSelect,
  CancelButton,
  SubmitButton,
  ButtonSelect
} from "imagus-components";
import ChipInput from "../../../global/components/ChipInput";
import { ErrosDisplay, setErrorMsg } from "../../containers/ErrosDisplay";
import QuoteTable from "./QuoteTable";
import {
  CompileDatabaseItemtoJsItem,
  getQuoteNotes,
  checkItemPriceRange,
  calculateRetailPrice,
  displayRetailPrice,
  totalPriceDescription,
  PriceFormat2Number,
  getRealPrice
} from "./QuoteCompileFunctions";
import AuthorisedLabel from "../companies/AuthorisedLabel";
import { isMomentAfter } from "../../../global/functions/DateTimeAmend";
import { propComparator } from "../../../global/functions/StringOperations";

import Grid from "@material-ui/core/Grid";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";
import InputAdornment from "@material-ui/core/InputAdornment";
import ErrorIcon from "@material-ui/icons/Error";

type CreateQuoteFormPropsType = {
  priceSheet: { [key: string]: * },
  productSheet?: { [key: string]: * },
  companies: Array<*>,
  onClose: Function,
  companyType: string,
  companyAuthority: boolean,
  createQuote: Function,
  editQuote: Function,
  quote?: { [key: string]: * },
  isAdmin?: boolean
};

type CreateQuoteFormStatesType = {
  errors: ErrorsType,
  loading: boolean,
  customer_name: string,
  authorised_reseller: boolean,
  qty: { [key: string]: number },
  type: string,
  tier: string,
  isNewCompany: boolean,
  company_id: string
};

const initialform: CreateQuoteFormStatesType = {
  errors: {},
  loading: false,
  customer_name: "",
  authorised_reseller: false,
  qty: {},
  type: "LONGTERM",
  tier: "",
  isNewCompany: false,
  company_id: ""
};

class CreateQuoteForm extends Component<
  CreateQuoteFormPropsType,
  CreateQuoteFormStatesType
> {
  csvArray: Array<*>;
  csvTotal: Array<*>;
  constructor(props: CreateQuoteFormPropsType) {
    super(props);
    this.submit = this.submit.bind(this);
    this.state = initialform;
    this.csvArray = [];
    this.csvTotal = [];
  }

  checkIsReseller = (): boolean => {
    return this.props.companyType === "RESELLER";
  };

  checkIsAuthorisedReseller = (): string => {
    let checked = false;
    if (this.checkIsReseller()) {
      checked = this.props.companyAuthority;
    }
    return checked;
  };

  componentDidMount() {
    let initials = { ...initialform };
    const quote = this.props.quote;
    if (quote) {
      if (quote.quotedCompany) {
        initials.company_id = quote.quotedCompany.id;
        let index = this.props.companies.findIndex(
          (x) => x.id === quote.quotedCompany.id
        );
        let inheritAuthority = this.props.companies[index].inheritAuthority;
        initials.authorised_reseller = inheritAuthority;
      } else {
        initials.customer_name = quote.company_name;
        initials.isNewCompany = true;
        initials.authorised_reseller = this.checkIsAuthorisedReseller();
      }

      initials.type = quote.licenceType;
    } else {
      initials.authorised_reseller = this.checkIsAuthorisedReseller();
    }

    this.setState({ ...initials, errors: {} }, (): * => {
      this.setInitial(true, quote ? true : false);
    });
  }

  setInitial = (setTier: boolean, isEdit?: boolean = false): * => {
    const priceSheet = this.props.priceSheet;
    const productSheet = this.props.productSheet;
    const price_list = priceSheet && priceSheet.parts.edges;
    const parts = this.props.quote && this.props.quote.parts;

    let qty = {};
    price_list.forEach((row, i) => {
      let sheetPart;
      if (productSheet) {
        let index =
          productSheet.sheetParts.edges &&
          productSheet.sheetParts.edges.findIndex(
            (item) => item.node.part.partNo === row.node.partNo
          );
        if (index > -1) {
          sheetPart = productSheet.sheetParts.edges[index].node;
        }
      }

      const item = CompileDatabaseItemtoJsItem(row.node, sheetPart);
      if (item.licenceType === this.state.type) {
        if (isEdit) {
          let index = parts.findIndex((x) => x.partno === item.partno);
          qty[item.partno] = index > -1 ? parts[index].quantity : 0;
        } else {
          qty[item.partno] = item.min;
        }
      }
    });
    this.csvArray = [];
    this.setState({ qty: qty });
    if (setTier) {
      this.setTierInitial(isEdit);
    }
  };

  setTierInitial = (isEdit?: boolean = false): * => {
    if (isEdit) {
      this.setState({ tier: this.props.quote.tierId });
    } else {
      const priceSheet = this.props.priceSheet;
      const tiers = priceSheet && priceSheet.tiers;
      tiers.sort(propComparator("name"));
      this.setState({ tier: tiers[0].id });
    }
  };

  handleTextChange = (e: Event & { currentTarget: HTMLInputElement }): * => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    this.setState({
      [name]: value
    });
  };

  handleQtyChange = (partNo: string): * => (
    e: Event & { currentTarget: HTMLInputElement }
  ): * => {
    const value = e.currentTarget.value;
    const qty = { ...this.state.qty };
    qty[partNo] = value;
    this.setState(
      {
        qty: qty
      },
      (): * => this.checkQtyValue(partNo)
    );
  };

  checkQtyValue = (name: string): * => {
    const elm = document.querySelector("input[name='" + name + "']");
    const valid = elm.getAttribute("aria-invalid");
    if (valid === "true") {
      this.setState({ errors: setErrorMsg(this.state.errors, name, "") });
    } else {
      this.setState({ errors: setErrorMsg(this.state.errors, name, "", true) });
    }
  };

  handleTypeChange = (e: Event): * => {
    const name = e.target.name;
    const value = e.target.value;
    this.setState(
      {
        [name]: value
      },
      (): * => {
        if (name === "type") this.setInitial(false);
      }
    );
  };

  handleSwitch = (name: string, value: string): * => {
    this.setState(
      {
        [name]: value
      },
      (): * => {
        if (value) {
          this.setState({
            authorised_reseller: this.checkIsAuthorisedReseller()
              ? true
              : false,
            company_id: ""
          });
        } else {
          this.setState({ customer_name: "" });
        }
      }
    );
  };

  syncChange = (name: string, value: *) => {
    this.setState(
      {
        [name]: value
      },
      (): * => {
        let index = this.props.companies.findIndex(
          (x) => x.id === this.state.company_id
        );
        let inheritAuthority =
          index > -1
            ? this.props.companies[index].inheritAuthority
            : this.checkIsAuthorisedReseller();
        this.setState({ authorised_reseller: inheritAuthority });
      }
    );
  };

  submit = async (e: Event): * => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((): * => ({ loading: true }));

    let parts = [];
    const qty = { ...this.state.qty };
    const array = [...this.csvArray];
    Object.keys(qty).forEach(function (key) {
      let index = array.findIndex((x) => x[0] === key);
      parts.push({
        partNo: key.toString(),
        quantity: qty[key],
        quotedRetailPrice: array[index][7],
        quotedDistributerPrice: array[index][8],
        quotedResellerPrice: array[index][9]
      });
    });

    if (this.props.quote) {
      let quoteParts = this.props.quote.parts;
      parts.map((part: *, i: number): * => {
        let index2 = quoteParts.findIndex((x) => x.partno === part.partNo);
        part["id"] = quoteParts[index2].id;
      });

      this.props.editQuote(
        this.props.quote.id,
        PriceFormat2Number(this.csvTotal[3]),
        PriceFormat2Number(this.csvTotal[5]),
        PriceFormat2Number(this.csvTotal[4]),
        this.state.authorised_reseller,
        parts,
        this.state.customer_name,
        this.state.company_id,
        this.state.tier,
        this.props.productSheet ? this.props.productSheet.id : "",
        this.submitDone,
        this.submitFail,
        this.props.isAdmin
      );
    } else {
      this.props.createQuote(
        PriceFormat2Number(this.csvTotal[3]),
        PriceFormat2Number(this.csvTotal[5]),
        PriceFormat2Number(this.csvTotal[4]),
        this.state.authorised_reseller,
        this.state.type,
        parts,
        this.state.customer_name,
        this.state.company_id,
        this.state.tier,
        this.props.productSheet ? this.props.productSheet.id : "",
        this.submitDone,
        this.submitFail
      );
    }
  };

  submitDone = () => {
    this.props.onClose();
  };

  submitFail = (errors: ErrorsType) => {
    this.setState({ errors: errors, loading: false });
  };

  render(): React$Element<*> {
    const errors = this.state.errors;
    const loading = this.state.loading;
    const licence_type = this.state.type;
    const tier = this.state.tier;
    const LicenceTypes = [
      { value: "LONGTERM", label: "Long Term Licence" },
      { value: "SHORTTERM", label: "Short Term Licence" }
    ];
    const CompanyList =
      this.props.companies &&
      this.props.companies.map((company: *, i: number): * => ({
        value: company.id,
        label: company.name
      }));

    const LicenceSumIds = [
      "IMAGUS-FR-001a",
      "IMAGUS-FR-001b",
      "IMAGUS-SD-001",
      "VIXVIZN-MK-001a",
      "VIXVIZN-CC-001a"
    ];

    let retail_sum = 0;
    let licence_sum = 0;
    let special_price = 0;

    const priceSheet = this.props.priceSheet;
    const productSheet = this.props.productSheet;
    const price_list = priceSheet && priceSheet.parts.edges;

    const tiers = priceSheet && priceSheet.tiers;
    let TierTypes = [];
    let DistributorDiscount = 0;
    let ResellerDiscount = 0;
    if (tiers) {
      tiers.sort(propComparator("name"));
      tiers.forEach((item) => {
        TierTypes.push({ value: item.id, label: item.name });
      });
      let index = null;
      if (tier !== "") {
        index = tiers.findIndex((x) => x.id === tier);
        if (this.props.quote && index < 0) {
          //when company tier changed and try to edit quote
          index = 0;
        }
      }
      if (index !== null) {
        DistributorDiscount = this.state.authorised_reseller
          ? tiers[index].discounts.authorised.distributer
          : tiers[index].discounts.standard.distributer;
        ResellerDiscount = this.state.authorised_reseller
          ? tiers[index].discounts.authorised.reseller
          : tiers[index].discounts.standard.reseller;
      }
    }

    let quoteDate = null;
    if (this.props.quote) {
      quoteDate = this.props.quote.updateDateOrigin
        ? this.props.quote.updateDateOrigin
        : this.props.quote.createDateOrigin;
    }

    const list =
      price_list &&
      price_list
        .filter((row) => row.node.licenceType === licence_type)
        .map((row, i): * => {
          let sheetPart;
          if (productSheet) {
            let index =
              productSheet.sheetParts.edges &&
              productSheet.sheetParts.edges.findIndex(
                (item) => item.node.part.partNo === row.node.partNo
              );
            if (index > -1) {
              sheetPart = productSheet.sheetParts.edges[index].node;
            }
          }

          const item = CompileDatabaseItemtoJsItem(row.node, sheetPart);
          const notes = getQuoteNotes(item.notes, item.min, item.max);
          const value = this.state.qty[item.partno];
          const price_check = checkItemPriceRange(value, item.min, item.max);
          let retail_price = "";
          if (price_check) {
            retail_price = calculateRetailPrice(
              item.type,
              value,
              item.hasRange,
              item.priceRanges,
              item.price,
              licence_sum
            );
            if (item.type === "PERCENTAGE_OF_TOTAL_LICENCE_FEE") {
              special_price = retail_price;
            }

            retail_sum += parseFloat(retail_price);
            if (LicenceSumIds.indexOf(item.partno) > -1) {
              licence_sum += parseFloat(retail_price);
            }
          }
          let displayPrice = true;
          if (licence_type === "SHORTTERM") {
            displayPrice = false;
          }
          const retail_float = getRealPrice(price_check, retail_price, 0);
          const dis_float = getRealPrice(
            price_check,
            retail_price,
            DistributorDiscount
          );
          const reseller_float = getRealPrice(
            price_check,
            retail_price,
            ResellerDiscount
          );
          const retail_price_display = displayRetailPrice(
            price_check,
            retail_price,
            0,
            displayPrice
          );
          const dis_price = displayRetailPrice(
            price_check,
            retail_price,
            DistributorDiscount,
            displayPrice
          );
          const reseller_price = displayRetailPrice(
            price_check,
            retail_price,
            ResellerDiscount,
            displayPrice
          );
          let index = this.csvArray.findIndex((x) => x[0] === item.partno);
          if (index < 0) {
            this.csvArray.push([
              item.partno,
              item.desctiotion,
              value,
              retail_price_display,
              dis_price,
              reseller_price,
              notes,
              retail_float,
              dis_float,
              reseller_float
            ]);
          } else {
            this.csvArray[index] = [
              item.partno,
              item.desctiotion,
              value,
              retail_price_display,
              dis_price,
              reseller_price,
              notes,
              retail_float,
              dis_float,
              reseller_float
            ];
          }

          return [
            item.partno,
            item.desctiotion,
            <StyledTextField
              isRequired={true}
              label=""
              name={item.partno}
              value={
                this.state.qty[item.partno] !== undefined
                  ? this.state.qty[item.partno]
                  : ""
              }
              onChange={this.handleQtyChange(item.partno)}
              inpuType="number"
              isInvalid={!price_check}
            />,
            retail_price_display,
            dis_price,
            reseller_price,
            notes
          ];
        });
    const retail_total =
      licence_type === "LONGTERM" ? retail_sum : special_price;
    const total_des = totalPriceDescription(
      this.state.authorised_reseller,
      this.state.qty["VIXVIZN-SM-001"],
      licence_type
    );
    const retail_sum_display = displayRetailPrice(true, retail_total, 0);
    const dis_sum = displayRetailPrice(true, retail_total, DistributorDiscount);
    const reseller_sum = displayRetailPrice(
      true,
      retail_total,
      ResellerDiscount
    );
    this.csvTotal = [
      "Total Price",
      total_des,
      "",
      retail_sum_display,
      dis_sum,
      reseller_sum,
      ""
    ];
    return (
      <form
        onSubmit={(!loading && this.submit) || undefined}
        className="app-wrapper"
      >
        <div className="app-panel">
          <div className="app-panel-content">
            <Grid container spacing={8}>
              <Grid item xs={12}>
                <ButtonSelect
                  value={this.state.isNewCompany}
                  name="isNewCompany"
                  buttons={[
                    { label: "Existing Company", value: false },
                    { label: "New Company", value: true }
                  ]}
                  handleChange={this.handleSwitch}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                {(this.state.isNewCompany && (
                  <StyledTextField
                    isRequired={true}
                    label="New Company Name"
                    name="customer_name"
                    value={this.state.customer_name}
                    onChange={this.handleTextChange}
                    inpuType="text"
                  />
                )) || (
                  <ChipInput
                    name="company_id"
                    label="Company"
                    isStyled
                    isRequired
                    placeholder="Select a Company"
                    tags={CompanyList}
                    value={this.state.company_id}
                    syncChange={this.syncChange}
                  />
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                style={{ height: "100px", lineHeight: "100px" }}
              >
                {this.state.isNewCompany && !this.checkIsReseller() && (
                  <Tooltip title="New company can only be standard reseller.">
                    <HelpIcon
                      className="grey-icon right-margin-10"
                      style={{ verticalAlign: "text-bottom" }}
                    />
                  </Tooltip>
                )}
                <AuthorisedLabel
                  status={this.state.authorised_reseller ? 1 : -1}
                />
              </Grid>
              {/*<Grid item xs={12} md={3}>
                <StyledSelect
                  value={licence_type}
                  onChange={this.handleTypeChange}
                  name="type"
                  label="Licence Type"
                  isRequired={true}
                  menus={LicenceTypes}
                  isStyled
                  isDisabled={this.props.quote !== undefined}
                />
              </Grid>*/}
              {(this.props.companyType === "TOPLEVEL" ||
                this.props.isAdmin) && (
                <Grid item xs={12} md={2}>
                  <StyledSelect
                    value={tier}
                    onChange={this.handleTypeChange}
                    name="tier"
                    label="Tier"
                    isRequired={false}
                    menus={TierTypes}
                    isStyled
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <QuoteTable
                  table={list}
                  authorised_reseller={this.state.authorised_reseller}
                  licence_type={this.state.type}
                  totalRow={[
                    "Total Price",
                    total_des,
                    "",
                    retail_sum_display,
                    dis_sum,
                    reseller_sum,
                    ""
                  ]}
                  companyType={this.props.companyType}
                  create_date={new Date()}
                  productSheetName={
                    this.props.productSheet &&
                    this.props.isAdmin &&
                    this.props.productSheet.description
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <ErrosDisplay errors={errors} form="create quote" />
              </Grid>
              <Grid item xs={12}>
                <div className="form-right-btn-block">
                  <CancelButton onClick={this.props.onClose} />
                  <SubmitButton
                    loading={loading}
                    title="Save"
                    disabled={Object.keys(errors).length > 0}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </form>
    );
  }
}

export default CreateQuoteForm;
